import Breadcrumb from "@/components/base/Breadcrumb";
import ContactForm from "@/pages/about/components/ContactForm";
import { useScrollToTop } from "@/hooks/useScrollToTop";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const values = [
  {
    icon: "ri-focus-3-line",
    title: "专注内容",
    desc: "以编辑视角筛选资讯，只留下值得花时间阅读的内容，避免信息噪音。",
    color: "bg-primary-100 text-primary-700",
  },
  {
    icon: "ri-layout-grid-line",
    title: "清晰分类",
    desc: "娱乐资讯、影视推荐、明星动态三大频道，让不同类型的需求各有归属。",
    color: "bg-accent-100 text-accent-800",
  },
  {
    icon: "ri-heart-3-line",
    title: "尊重读者",
    desc: "页面克制、阅读舒适，不打扰、不诱导，把注意力还给内容本身。",
    color: "bg-secondary-100 text-secondary-900",
  },
];

const team = [
  { role: "内容编辑部", desc: "负责资讯筛选、影视推荐与选题策划，保证内容准确与可读。", icon: "ri-quill-pen-line" },
  { role: "产品与设计", desc: "打磨阅读体验与信息结构，让浏览与查找更顺畅。", icon: "ri-palette-line" },
  { role: "技术与运营", desc: "维护平台稳定运行，并持续优化站内搜索与内容分发。", icon: "ri-code-s-slash-line" },
];

const contacts = [
  { icon: "ri-mail-line", label: "内容反馈", value: "contact@mitao33.com" },
  { icon: "ri-briefcase-line", label: "商务合作", value: "biz@mitao33.com" },
  { icon: "ri-weibo-fill", label: "官方微博", value: "@蜜桃33" },
  { icon: "ri-map-pin-line", label: "办公地址", value: "北京市朝阳区创意园 3 号楼 8 层" },
];

export default function AboutPage() {
  useScrollToTop();
  useDocumentMeta(
    "关于我们 - 蜜桃33",
    "了解蜜桃33的定位、内容范围与团队理念，并通过反馈表单与我们取得联系。",
  );

  return (
    <div className="w-full pb-16">
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1200px] px-4 py-8 md:px-6 md:py-12">
          <Breadcrumb items={[{ label: "关于我们" }]} />
          <div className="mt-5 max-w-3xl">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 text-xs font-semibold text-primary-700">
              <i className="ri-information-line" />
              关于蜜桃33
            </span>
            <h1 className="mt-4 font-heading text-3xl font-bold tracking-tight text-foreground-950 md:text-4xl">
              用编辑的视角，做值得阅读的娱乐内容
            </h1>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
              蜜桃33 是一个综合娱乐资讯平台，围绕娱乐新闻、影视推荐与明星动态三大频道，为关注娱乐内容的读者提供清晰、权威、好读的信息服务。
            </p>
          </div>
        </div>
      </div>

      <div className="mx-auto mt-12 w-full max-w-[1200px] px-4 md:px-6">
        <section className="grid grid-cols-1 items-center gap-8 lg:grid-cols-2" aria-label="网站介绍">
          <div className="overflow-hidden rounded-lg">
            <img
              src="https://readdy.ai/api/search-image?query=warm%20editorial%20desk%20flatlay%20with%20film%20reels%2C%20notebooks%20and%20a%20cup%20of%20coffee%20in%20soft%20amber%20afternoon%20light%2C%20clean%20minimalist%20composition%2C%20cinematic%20warm%20tone%2C%20high%20detail&width=1000&height=750&seq=mitao33-about-editorial&orientation=landscape"
              alt="蜜桃33 内容编辑部工作场景"
              title="蜜桃33 - 关于我们"
              className="h-full w-full object-cover"
            />
          </div>
          <div>
            <h2 className="font-heading text-2xl font-bold text-foreground-950">网站介绍</h2>
            <div className="mt-4 flex flex-col gap-4 text-[15px] leading-[1.9] text-foreground-700">
              <p>
                我们相信娱乐资讯不只是消遣。每天都有大量影视、音乐与行业信息涌现，真正有价值的部分往往被淹没。蜜桃33 希望做的，是替读者完成第一步筛选。
              </p>
              <p>
                在「娱乐资讯」频道，你会看到经过整理的行业要闻；在「影视推荐」频道，编辑会为你挑选值得一看的作品；在「明星动态」频道，我们关注创作者的作品与行程，而非无关的喧嚣。
              </p>
              <p>
                我们重视阅读体验本身：清晰的分类、克制的排版、明确的层级，都为了让浏览更轻松。
              </p>
            </div>
          </div>
        </section>

        <section className="mt-14" aria-label="我们的理念">
          <h2 className="font-heading text-2xl font-bold text-foreground-950">我们的理念</h2>
          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
            {values.map((item) => (
              <div key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-6">
                <span className={`flex h-12 w-12 items-center justify-center rounded-lg ${item.color}`}>
                  <i className={`${item.icon} text-2xl`} />
                </span>
                <h3 className="mt-4 text-base font-semibold text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-14" aria-label="团队简介">
          <h2 className="font-heading text-2xl font-bold text-foreground-950">团队简介</h2>
          <p className="mt-2 max-w-2xl text-sm text-foreground-600">
            一支喜欢娱乐内容、也认真对待内容的小团队，负责从选题到呈现的每一个环节。
          </p>
          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
            {team.map((item) => (
              <div key={item.role} className="rounded-lg bg-background-100 p-6">
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-background-50 text-primary-600">
                    <i className={`${item.icon} text-xl`} />
                  </span>
                  <h3 className="text-base font-semibold text-foreground-950">{item.role}</h3>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-14 grid grid-cols-1 gap-8 lg:grid-cols-[1fr_1fr]" aria-label="联系方式与反馈">
          <div>
            <h2 className="font-heading text-2xl font-bold text-foreground-950">联系方式</h2>
            <p className="mt-2 text-sm text-foreground-600">
              无论是内容建议还是合作洽谈，都欢迎通过以下方式与我们联系。
            </p>
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {contacts.map((item) => (
                <div
                  key={item.label}
                  className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-50 p-4"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                    <i className={`${item.icon} text-lg`} />
                  </span>
                  <div className="min-w-0">
                    <p className="text-xs text-foreground-500">{item.label}</p>
                    <p className="mt-0.5 break-words text-sm font-medium text-foreground-900">
                      {item.value}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <ContactForm />
        </section>
      </div>
    </div>
  );
}